import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { WindowRef } from '@spartacus/core';

/**
 * Следит за появлением и скрытием элемента из видимой области окна браузера.
 * Для использования необходимо добавить директиву к компоненту, а также подписаться
 * на событие intersected.
 *
 * ```<sd-component
 *      sdInViewport
 *      (intersected)="onEvent($event)"
 *    ></sd-component>
 * ```
 * `intersected` будет эмититься при скрытии и появлении элемента в видимой области окна браузера.
 * В `$event` будет `true` при появлении и `false` - при скрытии
 */

@Directive({
  selector: '[sdInViewport]',
  exportAs: 'sdInViewport',
  standalone: true,
})
export class InViewportDirective implements OnDestroy, AfterViewInit {
  // По умолчанию директива принимает threshold, если не указан, то 1
  @Input('sdInViewport') threashold = 1;
  // Эмитится каждый раз, когда элемент попадает или исчезает из зоны видимости
  @Output() intersected = new EventEmitter<boolean>();
  @Output() intersectedEntry = new EventEmitter<IntersectionObserverEntry>();
  // Если true intersected эмитится один раз при появлении элемента в зоне видимости и перестает следить за ним
  @Input() intersectedOnce = false;

  private intersectionObserver?: IntersectionObserver;
  private isSupported: boolean =
    isPlatformBrowser(this.platform) &&
    !!this.winRef.nativeWindow &&
    'IntersectionObserver' in this.winRef.nativeWindow;

  constructor(
    private elemRef: ElementRef<HTMLElement>,
    private ngZone: NgZone,
    private winRef: WindowRef,
    @Inject(PLATFORM_ID) private platform: any,
  ) {}

  ngAfterViewInit(): void {
    if (!this.isSupported) {
      return;
    }

    this.register();
  }

  register(): void {
    if (!this.isSupported) {
      return;
    }

    this.intersectionObserver = new IntersectionObserver(this.handleIntersectionChange, {
      threshold: typeof this.threashold === 'number' ? this.threashold : 1,
    });

    this.ngZone.runOutsideAngular(() => {
      if (this.intersectionObserver) {
        this.intersectionObserver.observe(this.elemRef.nativeElement);
      }
    });
  }

  unregister(): void {
    if (!this.isSupported || !this.intersectionObserver) {
      return;
    }

    this.intersectionObserver.unobserve(this.elemRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
  }

  private handleIntersectionChange = (entries: IntersectionObserverEntry[]): void => {
    entries.forEach((entry: IntersectionObserverEntry) => {
      if (this.intersectedOnce) {
        if (entry.isIntersecting) {
          this.intersected.emit(entry.isIntersecting);
          this.intersectedEntry.emit(entry);
          this.unregister();
        }
      } else {
        this.intersected.emit(entry.isIntersecting);
        this.intersectedEntry.emit(entry);
      }
    });
  };
}
